"use client";
import { useAtom } from "jotai";
import { AlertCircle, CheckCircle, Clock, Hash } from "lucide-react";
import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { parse } from "subtitletools";
import { addMessage } from "@/components/Messages";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { getDropFiles } from "@/lib/upload/file-drop";
import { cn } from "@/lib/utils";
import { RecentSubtitlesCards } from "./RecentSubtitlesCards";
import { addRecentSubtitle, recentSubtitlesAtom } from "./recentSubtitles";

interface SubtitleValidation {
  isValid: boolean;
  totalLines: number;
  duration: number;
  startTime: number;
  endTime: number;
  error?: string;
}

const formatTime = (milliseconds: number): string => {
  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);
  const ms = milliseconds % 1000;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
};

const SubtitleFileInput = ({
  onFileSelect,
}: {
  onFileSelect: (content: string, fileName?: string) => void;
}) => {
  return (
    <Dropzone
      getFilesFromEvent={getDropFiles}
      noPaste
      onDrop={(files) => {
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            onFileSelect(e.target?.result as string, files[0].name);
          };
          reader.readAsText(files[0]);
        }
      }}
      accept={{ "text/plain": [".srt"] }}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          <div
            className={cn({
              "w-full border-2 border-dashed border-foreground/80 px-4 py-16 font-sans rounded hover:border-foreground bg-foreground/5 hover:bg-transparent flex flex-col items-center justify-center text-foreground hover:text-foreground text-xl": true,
              "border-gray-400": isDragActive,
            })}
          >
            <p className="mb-4">Drop subtitle file here</p>
            <p className="text-base text-foreground/80">Click to select or drag and drop</p>
          </div>
        </div>
      )}
    </Dropzone>
  );
};

export const SubtitlesForm = ({ setInputSrt }: { setInputSrt: (value: string) => void }) => {
  return <SubtitleInputForm setInputSrt={setInputSrt} />;
};

export const SubtitleInputForm = ({ setInputSrt }: { setInputSrt: (value: string) => void }) => {
  const [validation, setValidation] = useState<SubtitleValidation | null>(null);
  const [subtitleText, setSubtitleText] = useState("");
  const [currentFileName, setCurrentFileName] = useState<string | undefined>();
  const [recentSubtitles, setRecentSubtitles] = useAtom(recentSubtitlesAtom);

  // Handle text input changes
  useEffect(() => {
    setSubtitleText(subtitleText);
    setInputSrt(subtitleText);
  }, [subtitleText, setInputSrt]);

  // Validate subtitle content whenever it changes
  useEffect(() => {
    if (!subtitleText.trim()) {
      setValidation(null);
      return;
    }

    try {
      const captions = parse(subtitleText);

      if (captions.length === 0) {
        setValidation({
          isValid: false,
          totalLines: 0,
          duration: 0,
          startTime: 0,
          endTime: 0,
          error: "No valid subtitle captions found",
        });
        return;
      }

      const startTime = Math.min(...captions.map((c) => c.start));
      const endTime = Math.max(...captions.map((c) => c.end));
      const duration = endTime - startTime;

      setValidation({
        isValid: true,
        totalLines: captions.length,
        duration,
        startTime,
        endTime,
      });
    } catch (err) {
      setValidation({
        isValid: false,
        totalLines: 0,
        duration: 0,
        startTime: 0,
        endTime: 0,
        error: err instanceof Error ? err.message : "Invalid subtitle format",
      });
    }
  }, [subtitleText]);

  const handleFileUpload = (content: string, fileName?: string) => {
    setSubtitleText(content);
    setCurrentFileName(fileName);
    setInputSrt(content);

    // Save to recent subtitles when a file is uploaded
    try {
      const captions = parse(content);
      if (captions.length > 0) {
        const startTime = Math.min(...captions.map((c) => c.start));
        const endTime = Math.max(...captions.map((c) => c.end));
        const duration = endTime - startTime;

        const displayName = fileName || `Subtitle with ${captions.length} captions`;
        setRecentSubtitles(addRecentSubtitle(content, displayName, captions.length, duration));
      }
    } catch (error) {
      console.warn("Failed to save uploaded subtitle:", error);
    }
  };

  const handleTextChange = (value: string) => {
    setSubtitleText(value);
    setCurrentFileName(undefined); // Clear filename when manually typing
    setInputSrt(value);
  };

  const handleRecentSelect = (content: string) => {
    setSubtitleText(content);
    setCurrentFileName(undefined); // Clear filename when selecting from recent
    setInputSrt(content);
  };

  return (
    <div className="space-y-4">
      {/* Recent Subtitles Cards */}
      <RecentSubtitlesCards onSelect={handleRecentSelect} />

      <div>
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Upload subtitles file (.srt)
        </label>
        <div className="mt-2">
          <SubtitleFileInput onFileSelect={handleFileUpload} />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Or paste subtitle content directly
        </label>
        <div className="mt-2">
          <Textarea
            value={subtitleText}
            placeholder="Paste your subtitle content here..."
            rows={12}
            className="font-mono text-sm"
            onChange={(e) => handleTextChange(e.target.value)}
          />
        </div>

        {/* Save button for pasted content */}
        {subtitleText.trim() && validation?.isValid && (
          <div className="mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (validation?.isValid) {
                  const displayName = `Pasted subtitle (${validation.totalLines} captions)`;
                  setRecentSubtitles(
                    addRecentSubtitle(
                      subtitleText,
                      displayName,
                      validation.totalLines,
                      validation.duration
                    )
                  );
                  addMessage({
                    text: "Saved to recent subtitles",
                    type: "SUCCESS",
                  });
                }
              }}
            >
              Save to Recent Subtitles
            </Button>
          </div>
        )}

        {/* Validation Status */}
        {validation && (
          <div
            className={`p-3 rounded-lg border mt-2 ${
              validation.isValid ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              {validation.isValid ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <span
                className={`font-medium ${validation.isValid ? "text-green-800" : "text-red-800"}`}
              >
                {validation.isValid ? "Valid SRT Format" : "Invalid Format"}
              </span>
            </div>

            {validation.isValid ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Hash className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700">
                    {validation.totalLines} caption
                    {validation.totalLines !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700">Duration: {formatTime(validation.duration)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-700">
                    Range: {formatTime(validation.startTime)} - {formatTime(validation.endTime)}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-red-700 text-sm">{validation.error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
